import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { catchError, of, Subject, Subscription, switchMap } from 'rxjs';
import {
  PaginatedReferralResponse,
  SaveReferralRequest,
  Referral,
  ReferralCountRequest,
  ReferralListRequest,
} from '../../Models/Referral';
import { ClientConfig } from '../../Models/Client-Nav';
import { ReferralService } from './referral.service';
import { RootScopeService } from '../../core/services/root-scope.service';

@Component({
  selector: 'app-referral',
  templateUrl: './referral.component.html',
  styleUrls: ['./referral.component.scss'],
  standalone: false,
})
export class ReferralComponent implements OnInit, OnDestroy {
  private static readonly ALL_SERVICE_TYPES_OPTION = 'All service types';
  private static readonly ALL_SERVICE_ACTIONS_OPTION = 'All service actions';

  @Output() addReferral = new EventEmitter<void>();
  @Output() deleteReferral = new EventEmitter<Referral>();

  public isFormOpen = false;
  public isReportsOpen = false;
  public formMode: 'create' | 'edit' = 'create';
  public isSaving = false;
  public saveErrorMessage = '';
  public saveSuccessMessage = '';
  public formModel: SaveReferralRequest = this.createEmptyFormModel();

  public referrals: Referral[] = [];
  public loading = false;
  public isExporting = false;
  public errorMessage = '';

  public pageSize = 10;
  public skip = 0;
  public total = this.pageSize;
  public hasMorePages = false;

  public searchText = '';
  public selectedTypeOfReferralFilters: string[] = [];
  public requestedDateFilter = 'last7';
  public customRequestedDateFrom = '';
  public customRequestedDateTo = '';

  public vendorNameOptions: string[] = [];
  public typeOfReferralOptions: string[] = [];
  public serviceTypeOptions: string[] = [];
  public serviceActionOptions: string[] = [];
  public filterDropdownOpen = false;
  public tempSelectedTypeOfReferralFilters: string[] = [];
  public tempSelectedServiceTypeFilters: string[] = [];
  public tempSelectedServiceActionFilters: string[] = [];
  public selectedServiceTypeFilters: string[] = [];
  public selectedServiceActionFilters: string[] = [];
  public selectedMissingDataFilter = '';
  public selectedServiceTypes: string[] = [];
  public isServiceTypeDropdownOpen = false;
  public serviceTypeDropdownTop = 0;
  public serviceTypeDropdownLeft = 0;
  public showDeleteConfirm = false;
  public referralToDelete: Referral | null = null;
  public showValidationErrors = false;
  public matchingCount: number | null = null;
  public countLoading = false;

  private hasTypeOfReferralConfig = false;
  private hasVendorNameConfig = false;
  private readonly countTrigger$ = new Subject<void>();
  private countRefreshSubscription: Subscription | null = null;
  private exportTriggerSubscription: Subscription | null = null;

  public vendorNameInput = '';
  public readonly allServiceTypesOption = ReferralComponent.ALL_SERVICE_TYPES_OPTION;
  public readonly allServiceActionsOption = ReferralComponent.ALL_SERVICE_ACTIONS_OPTION;
  public sortOrder: 'asc' | 'desc' = 'desc';
  public currentPage = 0;
  private pageStartKeys: Array<Record<string, unknown> | null> = [null];

  // Exempt actions that don't require Service ID for specific referral types
  private readonly exemptActions = ['Cancelled', 'No Plan', 'Not Applicable', 'Not Ready', 'SSN/DOB'];
  private readonly exemptReferralTypes = ['ISO', 'GBCS/GB Referral', 'ISO Captive Compliance'];
  // Other Vendor Referral: all actions are exempt (don't require Service ID)
  private readonly otherVendorReferralType ='Other Vendor Referral';


  constructor(
    private readonly referralService: ReferralService,
    private readonly router: Router,
    public readonly rootScope: RootScopeService
  ) {}

  public canAddReferral(): boolean {
    return this.rootScope.addGbReferralsVisible() === true;
  }

  public canDeleteReferral(): boolean {
    return this.rootScope.deleteGbReferralsVisible() === true;
  }

  public onRowClick(referral: Referral): void {
    if (!referral.id) return;
    this.router.navigate(['/ReferralDetail', referral.refNumber ?? referral.id], { queryParams: { id: referral.id } });
  }

  ngOnInit(): void {
    this.initializeCountRefresh();
    this.loadFilterOptionsFromConfig();
    this.loadPage(0);
    this.refreshReferralCount();
    this.syncExportButtonState();
    this.exportTriggerSubscription = this.referralService.exportTrigger$.subscribe(() => this.onExportReferrals());
  }

  ngOnDestroy(): void {
    this.countRefreshSubscription?.unsubscribe();
    this.countTrigger$.complete();
    this.exportTriggerSubscription?.unsubscribe();
  }

  public onSearchClick(): void {
    this.resetPaging();
    this.loadPage(0);
    this.refreshReferralCount();
  }

  public onOpenReports(): void {
    this.isReportsOpen = true;
  }

  public onCloseReports(): void {
    this.isReportsOpen = false;
  }

  public onFilterChange(): void {
    this.resetPaging();
    this.loadPage(0);
    this.refreshReferralCount();
  }

  public onRequestedDateFilterChange(): void {
    if (this.requestedDateFilter !== 'custom') {
      this.customRequestedDateFrom = '';
      this.customRequestedDateTo = '';
      this.onFilterChange();
    }
    this.syncExportButtonState();
  }

  public onCustomRequestedDateChange(): void {
    this.syncExportButtonState();

    if (this.requestedDateFilter !== 'custom') {
      return;
    }

    if (!this.customRequestedDateFrom || !this.customRequestedDateTo) {
      this.refreshReferralCount();
      return;
    }

    if (this.customRequestedDateFrom > this.customRequestedDateTo) {
      this.refreshReferralCount();
      return;
    }

    this.onFilterChange();
  }

  public get isExportDateRangeExceeded(): boolean {
    if (this.requestedDateFilter !== 'custom' || !this.customRequestedDateFrom || !this.customRequestedDateTo) {
      return false;
    }

    return this.monthsBetween(this.customRequestedDateFrom, this.customRequestedDateTo) > 6;
  }

  private monthsBetween(fromDateStr: string, toDateStr: string): number {
    const fromDate = new Date(fromDateStr);
    const toDate = new Date(toDateStr);
    let months = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    if (toDate.getDate() < fromDate.getDate()) {
      months--;
    }
    return months;
  }
 
public onSortByDate(): void {
  this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
  this.resetPaging();
  this.loadPage(0);  // reload from page 0 with new sort order
}
  public get isNewVendor(): boolean {
  const v = this.vendorNameInput.trim();
  return v.length > 0 && !this.vendorNameOptions.includes(v);
  }
  private isNewVendorPending: string | null = null;

private saveVendorToConfig(vendorName: string): void {
  this.referralService.addConfigValue('vendor_name', vendorName).subscribe({
    next: () => {
      if (!this.vendorNameOptions.includes(vendorName)) {
        this.vendorNameOptions = [...this.vendorNameOptions, vendorName]
          .sort((a, b) => a.localeCompare(b));
      }
    },
    error: (err: unknown) => {
      console.error('Failed to save new vendor to config', err);
    },
  });
}

  public onResetFilters(): void {
    this.searchText = '';
    this.selectedTypeOfReferralFilters = [];
    this.tempSelectedTypeOfReferralFilters = [];
    this.selectedServiceTypeFilters = [];
    this.tempSelectedServiceTypeFilters = [];
    this.selectedServiceActionFilters = [];
    this.tempSelectedServiceActionFilters = [];
    this.filterDropdownOpen = false;
    this.selectedMissingDataFilter = '';
    this.requestedDateFilter = 'last7';
    this.customRequestedDateFrom = '';
    this.customRequestedDateTo = '';
    this.sortOrder = 'desc';
    this.resetPaging();
    this.loadPage(0);
    this.refreshReferralCount();
    this.syncExportButtonState();
  }

  public onExportReferrals(): void {
    if (this.isExporting || this.isExportDateRangeExceeded) {
      return;
    }

    this.isExporting = true;
    this.syncExportButtonState();

    const payload = {
      search: this.searchText.trim() || null,
      filters: this.buildFilters(),
      sortOrder: this.sortOrder,
    };

    this.referralService.exportReferrals(payload).subscribe({
      next: (response) => {
        if (response && response.downloadURL) {
          window.open(response.downloadURL, '_blank');
        }
        this.isExporting = false;
        this.syncExportButtonState();
      },
      error: (error: unknown) => {
        console.error('Failed to export referrals', error);
        this.isExporting = false;
        this.syncExportButtonState();
      },
    });
  }

  private syncExportButtonState(): void {
    const visible = this.requestedDateFilter === 'custom' && !!this.customRequestedDateFrom && !!this.customRequestedDateTo;
    const disabled = this.isExporting || this.referrals.length === 0 || this.isExportDateRangeExceeded;
    const tooltip = this.isExportDateRangeExceeded ? 'Date range must be less than 6 months to export' : '';

    this.referralService.setExportButtonState({ visible, disabled, isExporting: this.isExporting, tooltip });
  }

  public onPageChange(event: PageChangeEvent): void {
    const targetPage = Math.floor(event.skip / this.pageSize);

    if (targetPage === this.currentPage) {
      return;
    }

    if (targetPage > this.currentPage + 1 || !this.pageStartKeys[targetPage]) {
      this.skip = this.currentPage * this.pageSize;
      return;
    }

    this.loadPage(targetPage);
  }

  public onAddNewReferral(): void {
    if (!this.canAddReferral()) {
      return;
    }
    this.addReferral.emit();
    this.openCreateForm();
  }

  public onEditRow(referral: Referral): void {
    if (!referral.id) return;
    this.router.navigate(['/ReferralDetail', referral.refNumber ?? referral.id], { queryParams: { id: referral.id, edit: true } });
  }

  public onDeleteRow(referral: Referral): void {
    if (!this.canDeleteReferral()) {
      return;
    }
    this.deleteReferral.emit(referral);

    if (!referral.id) {
      this.errorMessage = 'Unable to delete this referral because it has no ID.';
      return;
    }

    this.referralToDelete = referral;
    this.showDeleteConfirm = true;
  }

  public onConfirmDelete(): void {
    if (!this.canDeleteReferral()) {
      this.showDeleteConfirm = false;
      return;
    }

    if (!this.referralToDelete?.id) return;

    this.showDeleteConfirm = false;
    this.loading = true;
    this.errorMessage = '';
    const userId = this.rootScope.globals.currentUserId!;

    this.referralService.deleteReferral(this.referralToDelete.id, userId).subscribe({
      next: () => {
        this.referralToDelete = null;
        this.loadPage(this.currentPage);
        this.refreshReferralCount();
      },
      error: (error: unknown) => {
        this.loading = false;
        this.referralToDelete = null;
        this.errorMessage = 'Failed to delete referral.';
        console.error('Failed to delete referral', error);
      },
    });
  }

  public onCancelDelete(): void {
    this.showDeleteConfirm = false;
    this.referralToDelete = null;
  }

  public closeReferralForm(): void {
    if (this.isSaving) {
      return;
    }

    this.isFormOpen = false;
    this.showValidationErrors = false;
    this.saveErrorMessage = '';
    this.formModel = this.createEmptyFormModel();
  }

  public onSaveReferral(): void {
    if (this.formMode === 'edit' && !this.formModel.id) {
      this.saveErrorMessage = 'Unable to update referral because it has no ID.';
      return;
    }

    if (this.isRequiredFieldsMissing()) {
      this.showValidationErrors = true;
      this.saveErrorMessage = 'Type of Referral and Claim Number are required.';
      return;
    }

    this.isSaving = true;
    this.showValidationErrors = false;
    this.saveErrorMessage = '';
    this.isNewVendorPending = this.isNewVendor ? this.vendorNameInput.trim() : null;
    const payload = this.buildSavePayload();
    const userId = this.rootScope.globals.currentUserId!;
   
    const request$ =
      this.formMode === 'create'
        ? this.referralService.createReferral(payload, userId)
        : this.referralService.updateReferral(payload.id ?? '', payload, userId);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.isFormOpen = false;
        this.saveSuccessMessage =
          this.formMode === 'create'
            ? 'Referral created successfully.'
            : 'Referral updated successfully.';
        if (this.isNewVendorPending) {
        this.saveVendorToConfig(this.isNewVendorPending);
         this.isNewVendorPending = null;
        }
        this.loadPage(this.currentPage);
        this.refreshReferralCount();
      },
      error: (error: unknown) => {
        this.isSaving = false;
        this.saveErrorMessage =
          this.formMode === 'create'
            ? 'Failed to create referral.'
            : 'Failed to update referral.';
        console.error('Failed to save referral', error);
      },
    });
  }

  private loadPage(pageIndex: number): void {
    this.loading = true;
    this.errorMessage = '';

    const payload: ReferralListRequest = {
      pageSize: this.pageSize,
      lastEvaluatedKey: this.pageStartKeys[pageIndex] ?? null,
      search: this.searchText.trim() || null,
      filters: this.buildFilters(),
      sortOrder: this.sortOrder,
    };

    this.referralService.getReferralsPaginated(payload).subscribe({
      next: (response: PaginatedReferralResponse) => {
        this.applyResponse(pageIndex, response);
      },
      error: (error: unknown) => {
        this.loading = false;
        this.errorMessage = 'Failed to load referrals.';
        console.error('Failed to load referrals', error);
      },
    });
  }

  private initializeCountRefresh(): void {
    this.countRefreshSubscription = this.countTrigger$
      .pipe(
        switchMap(() => {
          this.countLoading = true;
          const payload: ReferralCountRequest = {
            search: this.searchText.trim() || null,
            filters: this.buildFilters(),
          };

          return this.referralService.getReferralsCount(payload).pipe(
            catchError((error: unknown) => {
              console.error('Failed to load referral count', error);
              return of(null);
            })
          );
        })
      )
      .subscribe((response) => {
        this.countLoading = false;
        this.matchingCount = response ? response.count : this.matchingCount;
      });
  }

  private refreshReferralCount(): void {
    this.countTrigger$.next();
  }

  private applyResponse(pageIndex: number, response: PaginatedReferralResponse): void {
    const items = Array.isArray(response.items) ? response.items : [];

    this.referrals = items;
    this.updateFilterOptions(this.referrals);
    this.currentPage = pageIndex;
    this.skip = this.currentPage * this.pageSize;
    this.hasMorePages = response.hasMorePages;

    this.pageStartKeys[pageIndex + 1] = response.lastEvaluatedKey ?? null;

    const visibleCount = this.skip + this.referrals.length;
    this.total = this.hasMorePages ? visibleCount + this.pageSize : visibleCount;

    this.loading = false;
    this.syncExportButtonState();
  }

  private buildFilters(): Record<string, string[]> | null {
    const filters: Record<string, string[]> = {};

    if (this.selectedTypeOfReferralFilters.length > 0) {
      filters['type_of_referral'] = [...this.selectedTypeOfReferralFilters];
    }

    if (this.selectedServiceTypeFilters.length > 0) {
      filters['serviceType'] = [...this.selectedServiceTypeFilters];
    }

    if (this.selectedServiceActionFilters.length > 0) {
      filters['status'] = [...this.selectedServiceActionFilters];
    }

    if (this.selectedMissingDataFilter) {
      filters['missing_data'] = [this.selectedMissingDataFilter];
    }

    const dateRange = this.getRequestedDateRange();
    if (dateRange) {
      filters['date_requested_from'] = [dateRange.from];
      filters['date_requested_to'] = [dateRange.to];
    }

    return Object.keys(filters).length > 0 ? filters : null;
  }

  private getRequestedDateRange(): { from: string; to: string } | null {
    if (this.requestedDateFilter === 'last7') {
      return this.buildRelativeDateRange(7);
    }

    if (this.requestedDateFilter === 'last30') {
      return this.buildRelativeDateRange(30);
    }

    if (this.requestedDateFilter === 'last90') {
      return this.buildRelativeDateRange(90);
    }

    if (this.requestedDateFilter === 'custom') {
      if (!this.customRequestedDateFrom || !this.customRequestedDateTo) {
        return null;
      }

      if (this.customRequestedDateFrom > this.customRequestedDateTo) {
        return null;
      }

      return {
        from: this.customRequestedDateFrom,
        to: this.customRequestedDateTo,
      };
    }

    return null;
  }

  private buildRelativeDateRange(days: number): { from: string; to: string } {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (days - 1));

    return {
      from: this.toDateOnlyString(startDate),
      to: this.toDateOnlyString(endDate),
    };
  }

  private toDateOnlyString(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private resetPaging(): void {
    this.currentPage = 0;
    this.skip = 0;
    this.total = this.pageSize;
    this.hasMorePages = false;
    this.pageStartKeys = [null];
  }

  private updateFilterOptions(items: Referral[]): void {
    if (this.hasTypeOfReferralConfig) {
      return;
    }

    const typeSet = new Set<string>(this.typeOfReferralOptions);

    for (const item of items) {
      const type = item.typeOfReferral?.trim();

      if (type && !this.hasTypeOfReferralConfig) {
        typeSet.add(type);
      }
    }

    if (!this.hasTypeOfReferralConfig) {
      this.typeOfReferralOptions = Array.from(typeSet).sort((a, b) => a.localeCompare(b));
    }
  }

  private loadFilterOptionsFromConfig(): void {
    this.referralService.getConfigList().subscribe({
      next: (configs: ClientConfig[]) => {
        const refreshConfig = configs.find((config) => config.configKey === 'last_referral_run');
        const refreshDate = refreshConfig?.configValues
          ?.map((value) => value?.trim())
          .find((value): value is string => Boolean(value)) || '';
        this.referralService.setReferralRefreshDate(refreshDate || 'N/A');

        const typeOptions = this.getConfigOptions(configs, ['referralType']);

        if (typeOptions.length) {
          this.typeOfReferralOptions = typeOptions;
          this.hasTypeOfReferralConfig = true;
        }
        const vendorOptions = this.getConfigOptions(configs, ['referral_vendors']);
        if (vendorOptions.length) {
         this.vendorNameOptions = vendorOptions;
         this.hasVendorNameConfig = true;
         }
        this.serviceTypeOptions = this.getConfigOptions(configs, ['service_types'], [ReferralComponent.ALL_SERVICE_TYPES_OPTION]);
        this.serviceActionOptions = this.getConfigOptions(configs, ['service_actions'], [ReferralComponent.ALL_SERVICE_ACTIONS_OPTION]);
      },
      error: (error: unknown) => {
        console.error('Failed to load referral filter options from config', error);
      },
    });
  }

  private getConfigOptions(configs: ClientConfig[], aliases: string[], excludedOptions: string[] = []): string[] {
    const normalizedAliases = aliases.map((alias) => this.normalizeConfigKey(alias));
    const excludedOptionKeys = new Set(excludedOptions.map((option) => this.normalizeConfigKey(option)));

    const matched = configs.find((config) => {
      const key = this.normalizeConfigKey(config.configKey);
      const name = this.normalizeConfigKey(config.configName);
      return normalizedAliases.includes(key) || normalizedAliases.includes(name);
    });

    if (!matched || !Array.isArray(matched.configValues)) {
      return [];
    }

    const values = matched.configValues
      .map((value) => value?.trim())
      .filter((value): value is string => Boolean(value))
      .filter((value) => !excludedOptionKeys.has(this.normalizeConfigKey(value)));

    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }

  private normalizeConfigKey(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase().replace(/[_\s-]/g, '');
  }

  public toggleServiceTypeDropdown(event: MouseEvent): void {
    if (this.isServiceTypeDropdownOpen) {
      this.isServiceTypeDropdownOpen = false;
      return;
    }
    const input = (event.target as HTMLElement).closest('[data-dropdown-scope="true"]')?.querySelector('input') as HTMLElement;
    const el = input || (event.target as HTMLElement);
    const rect = el.getBoundingClientRect();
    this.serviceTypeDropdownTop = rect.bottom + 2;
    this.serviceTypeDropdownLeft = rect.left;
    this.isServiceTypeDropdownOpen = true;
  }

  public onServiceTypeCheckboxChange(option: string, checked: boolean): void {
    if (option === this.allServiceTypesOption) {
      this.selectedServiceTypes = checked ? [...this.serviceTypeOptions] : [];
      return;
    }

    if (checked && !this.selectedServiceTypes.includes(option)) {
      this.selectedServiceTypes = [...this.selectedServiceTypes, option];
    } else if (!checked) {
      this.selectedServiceTypes = this.selectedServiceTypes.filter(s => s !== option);
    }
  }

  public toggleFilterDropdown(): void {
    this.filterDropdownOpen = !this.filterDropdownOpen;

    if (this.filterDropdownOpen) {
      this.tempSelectedTypeOfReferralFilters = [...this.selectedTypeOfReferralFilters];
      this.tempSelectedServiceTypeFilters = [...this.selectedServiceTypeFilters];
      this.tempSelectedServiceActionFilters = [...this.selectedServiceActionFilters];
    }
  }

  public onFilterCheckboxChange(
    filterKey: 'typeOfReferral' | 'serviceType' | 'serviceAction',
    option: string,
    checked: boolean
  ): void {
    const target = this.getTempFilterValues(filterKey);

    if (filterKey === 'serviceType' && option === this.allServiceTypesOption) {
      this.tempSelectedServiceTypeFilters = checked ? [...this.serviceTypeOptions] : [];
      return;
    }

    if (filterKey === 'serviceAction' && option === this.allServiceActionsOption) {
      this.tempSelectedServiceActionFilters = checked ? [...this.serviceActionOptions] : [];
      return;
    }

    if (checked) {
      if (!target.includes(option)) {
        target.push(option);
      }
      return;
    }

    const index = target.indexOf(option);
    if (index >= 0) {
      target.splice(index, 1);
    }
  }

  public clearTempFilters(): void {
    this.tempSelectedTypeOfReferralFilters = [];
    this.tempSelectedServiceTypeFilters = [];
    this.tempSelectedServiceActionFilters = [];
  }

  public applyFilters(): void {
    this.selectedTypeOfReferralFilters = [...this.tempSelectedTypeOfReferralFilters];
    this.selectedServiceTypeFilters = [...this.tempSelectedServiceTypeFilters];
    this.selectedServiceActionFilters = [...this.tempSelectedServiceActionFilters];
    this.filterDropdownOpen = false;
    this.onFilterChange();
  }

  public activeFilterChips(): Array<{ key: 'typeOfReferral' | 'serviceType' | 'serviceAction'; value: string }> {
    return [
      ...this.selectedTypeOfReferralFilters.map((value) => ({ key: 'typeOfReferral' as const, value })),
      ...this.selectedServiceTypeFilters.map((value) => ({ key: 'serviceType' as const, value })),
      ...this.selectedServiceActionFilters.map((value) => ({ key: 'serviceAction' as const, value })),
    ];
  }

  public removeFilterChip(key: 'typeOfReferral' | 'serviceType' | 'serviceAction', value: string): void {
    if (key === 'typeOfReferral') {
      this.selectedTypeOfReferralFilters = this.selectedTypeOfReferralFilters.filter((item) => item !== value);
      this.tempSelectedTypeOfReferralFilters = [...this.selectedTypeOfReferralFilters];
    } else if (key === 'serviceType') {
      this.selectedServiceTypeFilters = this.selectedServiceTypeFilters.filter((item) => item !== value);
      this.tempSelectedServiceTypeFilters = [...this.selectedServiceTypeFilters];
    } else {
      this.selectedServiceActionFilters = this.selectedServiceActionFilters.filter((item) => item !== value);
      this.tempSelectedServiceActionFilters = [...this.selectedServiceActionFilters];
    }

    this.onFilterChange();
  }

  public onRemoveFilterChip(
    event: MouseEvent,
    key: 'typeOfReferral' | 'serviceType' | 'serviceAction',
    value: string
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.removeFilterChip(key, value);
  }

  public handleRemoveFilter(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target as HTMLElement | null;
    const button = target?.closest('button[data-filter-key][data-filter-value]') as HTMLButtonElement | null;
    if (!button) {
      return;
    }

    const key = button.dataset['filterKey'];
    const value = button.dataset['filterValue'];
    if (!key || !value) {
      return;
    }

    if (key === 'typeOfReferral' || key === 'serviceType' || key === 'serviceAction') {
      this.removeFilterChip(key, value);
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-dropdown-scope="true"]')) return;
    if (target?.closest('.service-type-dropdown')) return;
    this.isServiceTypeDropdownOpen = false;
    if (target?.closest('[data-referral-filter-scope="true"]')) return;
    if (target?.closest('.referral-filter-dropdown')) return;
    this.filterDropdownOpen = false;
  }

  private getTempFilterValues(
    filterKey: 'typeOfReferral' | 'serviceType' | 'serviceAction'
  ): string[] {
    if (filterKey === 'typeOfReferral') {
      return this.tempSelectedTypeOfReferralFilters;
    }

    if (filterKey === 'serviceType') {
      return this.tempSelectedServiceTypeFilters;
    }

    return this.tempSelectedServiceActionFilters;
  }

  private openCreateForm(): void {
    this.formMode = 'create';
    this.showValidationErrors = false;
    this.saveErrorMessage = '';
    this.saveSuccessMessage = '';
    this.formModel = this.createEmptyFormModel();
    this.selectedServiceTypes = [];
     this.vendorNameInput = '';
    this.isFormOpen = true;
  }

  private createEmptyFormModel(): SaveReferralRequest {
    return {
      id: null,
      refNumber: null,
      dateRequested: null,
      enteredTs: null,
      active: null,
      vettingDate: null,
      typeOfReferral: null,
      claimantName: null,
      claimNumber: null,
      vendorName: null,
      serviceTypes: null,
      createdBy: null,
      createdAt: null,
      lastUpdatedBy: null,
      lastUpdatedAt: null,
    };
  }

  private buildSavePayload(): SaveReferralRequest {
    const validServices = this.selectedServiceTypes
      .map((serviceType) => serviceType.trim())
      .filter((serviceType) => serviceType.length > 0)
      .map((serviceType) => ({
        serviceType,
        action: '',
        serviceDataID: null,
        invoiceAmount: null,
        comments: null,
      }));

    return {
      ...this.formModel,
      id: this.toNullableString(this.formModel.id),
      refNumber: this.toNullableString(this.formModel.refNumber),
      dateRequested: this.toNullableString(this.formModel.dateRequested),
      enteredTs: this.toNullableString(this.formModel.enteredTs),
      active: this.formModel.active,
      vettingDate: this.toNullableString(this.formModel.vettingDate),
      typeOfReferral: this.toNullableString(this.formModel.typeOfReferral),
      claimantName: this.toNullableString(this.formModel.claimantName),
      claimNumber: this.toNullableString(this.formModel.claimNumber),
      vendorName: this.toNullableString(this.vendorNameInput),
      services: validServices,
      serviceTypes: this.selectedServiceTypes.length > 0 ? this.selectedServiceTypes.join(', ') : null,
      createdBy: this.toNullableString(this.formModel.createdBy),
      createdAt: this.toNullableString(this.formModel.createdAt),
      lastUpdatedBy: this.toNullableString(this.formModel.lastUpdatedBy),
      lastUpdatedAt: this.toNullableString(this.formModel.lastUpdatedAt),
    };
  }

  private toNullableString(value: string | null | undefined): string | null {
    const trimmed = (value ?? '').trim();
    return trimmed.length ? trimmed : null;
  }

  public isReferralFieldInvalid(field: 'typeOfReferral' | 'claimNumber'): boolean {
    if (!this.showValidationErrors) {
      return false;
    }

    return this.isMissingRequiredValue(this.formModel[field]);
  }

  private isRequiredFieldsMissing(): boolean {
    return this.isMissingRequiredValue(this.formModel.typeOfReferral)
      || this.isMissingRequiredValue(this.formModel.claimNumber);
  }

  private isMissingRequiredValue(value: string | null | undefined): boolean {
    return !value || value.trim().length === 0;
  }

  private toDateInputValue(value: string | null | undefined): string | null {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed.toISOString().slice(0, 10);
  }

  public getServiceDisplayEntries(referral: Referral): Array<{ serviceType: string; action: string | null; serviceDataID: string | null }> {
    const services = Array.isArray(referral.services) ? referral.services : [];

    if (services.length) {
      return services.map((service) => ({
        serviceType: (service.serviceType ?? '').trim() || 'Unknown Service',
        action: (service.action ?? '').trim() || null,
        serviceDataID: (service.serviceDataID ?? '').trim() || null,
      }));
    }

    const fallbackServiceTypes = (referral.serviceTypes ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value.length > 0);

    return fallbackServiceTypes.map((serviceType) => ({
      serviceType,
      action: null,
      serviceDataID: null,
    }));
  }
  public previousPage(): void {
  if (this.currentPage === 0) {
    return;
  }

  this.loadPage(this.currentPage - 1);
}

public nextPage(): void {
  if (!this.hasMorePages) {
    return;
  }

  this.loadPage(this.currentPage + 1);
}

public IsServiceIdRequiredForService(referral: Referral, service: { serviceType: string | null; action: string | null; serviceDataID: string | null }): boolean {
  const referralType = referral.typeOfReferral?.trim() ?? '';
  const action = service.action?.trim() ?? '';

  // Other Vendor Referral: all actions are exempt (don't require Service ID)
 if(referralType.toLowerCase() === this.otherVendorReferralType.toLowerCase()) {
    return false;
  }

  // Exempt actions that don't require Service ID for specific referral types
  if (this.exemptReferralTypes.some(type => type.toLowerCase() === referralType.toLowerCase())) {
    return !this.exemptActions.some(exempt => exempt.toLowerCase() === action.toLowerCase());
  }

  // All other cases require Service ID
  return true;


}
}
